import { useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { PartyPopper, Heart } from "lucide-react";

function playJoinSound() {
  try {
    const ctx = new AudioContext();
    // Upbeat ascending melody — "we're waiting for you!"
    const notes = [
      { freq: 523.25, start: 0, dur: 0.12 },     // C5
      { freq: 587.33, start: 0.1, dur: 0.12 },    // D5
      { freq: 659.25, start: 0.2, dur: 0.12 },    // E5
      { freq: 783.99, start: 0.3, dur: 0.15 },    // G5
      { freq: 880.0, start: 0.45, dur: 0.15 },    // A5
      { freq: 1046.5, start: 0.6, dur: 0.4 },     // C6 (held)
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "triangle";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.18, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    });
    setTimeout(() => ctx.close(), 2000);
  } catch {
    // ignore
  }
}

function playDeclineSound() {
  try {
    const ctx = new AudioContext();
    // Gentle descending — warm "we'll miss you"
    const notes = [
      { freq: 783.99, start: 0, dur: 0.2 },    // G5
      { freq: 659.25, start: 0.18, dur: 0.2 },  // E5
      { freq: 523.25, start: 0.36, dur: 0.4 },  // C5 (held)
    ];
    notes.forEach(({ freq, start, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.12, ctx.currentTime + start);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(ctx.currentTime + start);
      osc.stop(ctx.currentTime + start + dur + 0.05);
    });
    setTimeout(() => ctx.close(), 2000);
  } catch {
    // ignore
  }
}

interface RsvpCelebrationProps {
  response: "yes" | "no" | "waitlist";
  recipientName?: string;
  onDone: () => void;
}

const RsvpCelebration = ({ response, recipientName, onDone }: RsvpCelebrationProps) => {
  const isJoining = response === "yes" || response === "waitlist";

  const fireConfetti = useCallback(() => {
    if (!isJoining) return;
    const duration = 2200;
    const end = Date.now() + duration;

    confetti({
      particleCount: 80,
      spread: 100,
      origin: { y: 0.6 },
      colors: ["hsl(275,93%,29%)", "#f5e6d3", "#e8d5c0", "#d4a574", "#c4956a"],
    });

    const frame = () => {
      confetti({
        particleCount: 2,
        angle: 60,
        spread: 55,
        origin: { x: 0, y: 0.7 },
        colors: ["hsl(275,93%,29%)", "#f5e6d3", "#e8d5c0"],
      });
      confetti({
        particleCount: 2,
        angle: 120,
        spread: 55,
        origin: { x: 1, y: 0.7 },
        colors: ["hsl(275,93%,29%)", "#f5e6d3", "#e8d5c0"],
      });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
  }, [isJoining]);

  useEffect(() => {
    if (isJoining) {
      fireConfetti();
      playJoinSound();
    } else {
      playDeclineSound();
    }
  }, [isJoining, fireConfetti]);

  // Auto-dismiss after a delay
  useEffect(() => {
    const timer = setTimeout(onDone, 4000);
    return () => clearTimeout(timer);
  }, [onDone]);

  const title = isJoining
    ? response === "waitlist"
      ? "You're on the waitlist! 🤞"
      : "You're In! 🎉"
    : "We'll Miss You 💛";

  const subtitle = isJoining
    ? response === "waitlist"
      ? "We'll let you know if a spot opens up"
      : `Can't wait to see you${recipientName ? ` at ${recipientName}'s celebration` : ""}!`
    : `Thanks for letting us know. ${recipientName ? `${recipientName} will` : "We'll"} miss you 💕`;

  return (
    <motion.div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-background/95 backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <div className="flex flex-col items-center text-center px-8 space-y-6">
        {/* Animated icon */}
        <motion.div
          className={`w-24 h-24 rounded-full flex items-center justify-center ${
            isJoining ? "bg-primary/10" : "bg-accent/30"
          }`}
          initial={{ scale: 0, rotate: -20 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 14, delay: 0.15 }}
        >
          <motion.div
            className={`w-16 h-16 rounded-full flex items-center justify-center ${
              isJoining ? "bg-primary" : "bg-accent"
            }`}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 14, delay: 0.35 }}
          >
            {isJoining ? (
              <PartyPopper className="text-primary-foreground" size={30} strokeWidth={2.5} />
            ) : (
              <Heart className="text-accent-foreground" size={28} strokeWidth={2.5} />
            )}
          </motion.div>
        </motion.div>


        {/* Text */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.4 }}
          className="space-y-2"
        >
          <h2 className="text-3xl font-serif font-bold text-foreground">
            {title}
          </h2>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">
            {subtitle}
          </p>
        </motion.div>

        {/* Done button */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.3 }}
        >
          <Button
            onClick={onDone}
            className="rounded-full px-10 h-12 text-base bg-primary text-primary-foreground hover:bg-primary/90"
            size="lg"
          >
            {isJoining ? "Let's Go!" : "Done"}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default RsvpCelebration;
